import { ApiError } from "@/api/client";

type ErrorPathPart = string | number;

export interface ApiErrorDomainRule {
  code: string;
  status: number;
  detail?: unknown;
  matches?: (detail: unknown, body: unknown) => boolean;
  message: string;
}

export interface ApiErrorFieldRule {
  path: readonly ErrorPathPart[];
  field: string;
  message: string;
}

export interface ApiErrorPolicyOptions {
  fallbackMessage: string;
  domainRules?: readonly ApiErrorDomainRule[];
  fieldRules?: readonly ApiErrorFieldRule[];
}

export type ApiErrorPresentation =
  | { kind: "field"; issues: { field: string; message: string }[]; requestId: string | null }
  | { kind: "domain"; code: string; message: string; requestId: string | null }
  | { kind: "fallback"; message: string; requestId: string | null };

export interface ApiErrorNoticeData {
  message: string;
  requestId: string | null;
}

interface StructuredError {
  loc?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function pathsEqual(actual: unknown, expected: readonly ErrorPathPart[]): boolean {
  return (
    Array.isArray(actual) &&
    actual.length === expected.length &&
    actual.every((part, index) => part === expected[index])
  );
}

function structuredErrors(body: unknown): StructuredError[] {
  if (!isRecord(body) || !Array.isArray(body.errors)) return [];
  return body.errors.filter(isRecord);
}

export function presentApiError(
  error: unknown,
  options: ApiErrorPolicyOptions,
): ApiErrorPresentation {
  if (!(error instanceof ApiError)) {
    return { kind: "fallback", message: options.fallbackMessage, requestId: null };
  }

  const fieldIssues = (options.fieldRules ?? []).flatMap((rule) =>
    structuredErrors(error.body).some((candidate) => pathsEqual(candidate.loc, rule.path))
      ? [{ field: rule.field, message: rule.message }]
      : [],
  );
  if (fieldIssues.length > 0) {
    return { kind: "field", issues: fieldIssues, requestId: error.requestId };
  }

  const domainRule = (options.domainRules ?? []).find(
    (rule) =>
      rule.status === error.status &&
      (rule.matches?.(error.detail, error.body) ??
        ("detail" in rule && Object.is(rule.detail, error.detail))),
  );
  if (domainRule) {
    return {
      kind: "domain",
      code: domainRule.code,
      message: domainRule.message,
      requestId: error.requestId,
    };
  }

  return { kind: "fallback", message: options.fallbackMessage, requestId: error.requestId };
}

export function apiErrorNotice(error: unknown, options: ApiErrorPolicyOptions): ApiErrorNoticeData {
  const presentation = presentApiError(error, options);
  return {
    message: presentation.kind === "field" ? options.fallbackMessage : presentation.message,
    requestId: presentation.requestId,
  };
}
